import React, { useEffect, useMemo, useState } from 'react';
import { Box, Container, Typography, Card, CardContent, CircularProgress, Alert, Grid, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import RequireAuth from '../../components/athletes/RequireAuth';
import { useAuth } from '../../context/AuthContext';
import { listMyTeams } from '../../services/teamService';
import { roleLabel, sortRoles } from '../../utils/teamRoles';
import { seasonStatusLabel, seasonStatusChipStyle } from '../../utils/seasonStatus';
import { formatTeamSummaryLine } from '../../utils/teamClassification';

function TeamCard({ team, navigate }) {
  // Archived teams stay in Current/History exactly as before (027) — just
  // visually de-emphasized via reduced opacity (028), same as the
  // Program Dashboard's own SeasonTeamCard.
  const isArchived = team.status === 'archived';
  return (
    <Card
      onClick={() => navigate(`/my-teams/${team.id}`)}
      sx={{
        cursor: 'pointer', backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a', height: '100%',
        opacity: isArchived ? 0.6 : 1, '&:hover': { borderColor: 'rgba(46,168,75,0.4)' },
      }}
    >
      <CardContent>
        <Typography sx={{ fontWeight: 800, fontSize: '1.05rem' }}>{team.team_name}</Typography>
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem', mt: 0.25 }}>
          {formatTeamSummaryLine(team)}
        </Typography>
        <Typography sx={{ color: '#6a8aa8', fontSize: '0.8rem' }}>
          {team.city}, {team.state}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
          {team.season_status && (
            <Chip size="small" label={seasonStatusLabel(team.season_status)} sx={{ fontWeight: 700, ...seasonStatusChipStyle(team.season_status) }} />
          )}
          {sortRoles(team.roles).map(r => (
            <Chip key={r} size="small" label={roleLabel(r)} sx={{ bgcolor: '#0a2a14', color: '#2ea84b', fontWeight: 700 }} />
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

function TeamsInner() {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!currentUser) return;
    listMyTeams(currentUser.id)
      .then(setTeams)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [currentUser]);

  // Current vs. History follows teams.season_status (027) — 'ended' is
  // historical, everything else (setup/preseason/active/closeout, or no
  // season_status at all for a pre-022 row) is current. Deliberately not
  // teams.status (active/archived) — a separate, manual admin-visibility
  // toggle, not the competitive-season lifecycle this grouping reflects.
  const currentTeams = useMemo(() => teams.filter(t => t.season_status !== 'ended'), [teams]);
  const historyTeams = useMemo(() => teams.filter(t => t.season_status === 'ended'), [teams]);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 900 }}>My Teams</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress sx={{ color: '#2ea84b' }} /></Box>
      ) : teams.length === 0 ? (
        // Legacy standalone Team creation ("+ Create Team" → /my-teams/new
        // → CreateTeamPage/create_team_with_admin()) is no longer exposed
        // here (030 cleanup) — normal Season Team creation now happens
        // under a Program (My Programs → Program Dashboard → Create
        // Season Team). create_team_with_admin() and /my-teams/new
        // themselves are untouched; this is a presentation-only change.
        <Card sx={{ backgroundColor: 'rgba(10,20,34,0.8)', border: '1px solid #1e2a3a' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography sx={{ fontSize: '2.2rem', mb: 1.5 }}>🏟️</Typography>
            <Typography sx={{ fontWeight: 700, mb: 1 }}>No teams yet</Typography>
            <Typography sx={{ color: '#9eb8d0' }}>Season Teams are created from a Program. Visit My Programs to create one.</Typography>
          </CardContent>
        </Card>
      ) : (
        <>
          <Grid container spacing={2}>
            {currentTeams.map(t => (
              <Grid item xs={12} sm={6} key={t.id}>
                <TeamCard team={t} navigate={navigate} />
              </Grid>
            ))}
          </Grid>

          {historyTeams.length > 0 && (
            <Box sx={{ mt: 4 }}>
              <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', letterSpacing: '0.06em', color: '#e8f0f8', mb: 1.5 }}>
                HISTORY
              </Typography>
              <Grid container spacing={2}>
                {historyTeams.map(t => (
                  <Grid item xs={12} sm={6} key={t.id}>
                    <TeamCard team={t} navigate={navigate} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      )}
    </Container>
  );
}

export default function MyTeamsPage() {
  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <RequireAuth><TeamsInner /></RequireAuth>
    </Box>
  );
}
